import supabase from '../../supabase-client';

export class DashboardServiceSQL {
  static async getTotalBooks() {
    const { count, error } = await supabase
      .from('productbook')
      .select('*', { count: 'exact', head: true })
;
    return error ? 0 : count || 0;
  }

  static async getTotalAuthors() {
    const { count, error } = await supabase
      .from('authorsbook')
      .select('*', { count: 'exact', head: true });
    return error ? 0 : count || 0;
  }

  static async getTotalCategories() {
    const { count, error } = await supabase
      .from('categoriesbook')
      .select('*', { count: 'exact', head: true });
    return error ? 0 : count || 0;
  }

  static async getBooksByCategory() {
    try {
      // Obtener todas las categorías directamente
      const { data: allCategories, error: catError } = await supabase
        .from('categoriesbook')
        .select('id_category, name_category')
        .order('name_category', { ascending: true });

      console.log('📊 DASHBOARD DEBUG - Categories data:', { allCategories, catError });

      if (catError) {
        console.error('❌ Categories error:', catError);
        return [];
      }

      // Mapear categorías para mostrar en dashboard
      const result = allCategories?.map(cat => ({
        name: cat.name_category,
        id: cat.id_category
      })) || [];

      console.log('📊 Final category stats:', result);
      return result;
    } catch (error) {
      console.error('❌ getBooksByCategory error:', error);
      return [];
    }
  }

  static async getBooksByAuthor() {
    try {
      // Obtener todos los autores directamente
      const { data: allAuthors, error: authorsError } = await supabase
        .from('authorsbook')
        .select('id_author, name_author')
        .order('name_author', { ascending: true });

      console.log('📊 DASHBOARD DEBUG - Authors data:', { allAuthors, authorsError });

      if (authorsError) {
        console.error('❌ Authors error:', authorsError);
        return [];
      }

      // Mapear autores para mostrar en dashboard
      const result = allAuthors?.map(author => ({
        author: author.name_author,
        id: author.id_author
      })) || [];

      console.log('📊 Final author stats:', result);
      return result;
    } catch (error) {
      console.error('❌ getBooksByAuthor error:', error);
      return [];
    }
  }

  static async getBooksOnSale() {
    const { data, error } = await supabase
      .from('productbook')
      .select('*')
      .not('price_original_product', 'is', null)
      .gt('price_original_product', 0);
    return error ? [] : data;
  }

  static async getRecentBooks() {
    try {
      // Get recent products from all three tables
      const [booksData, biblesData, thingsData] = await Promise.all([
        // Recent books
        supabase
          .from('productbook')
          .select('*')
          .order('id_product', { ascending: false })
          .limit(5),
        
        // Recent bibles
        supabase
          .from('biblebook')
          .select('*')
          .order('id_bible', { ascending: false })
          .limit(5),
        
        // Recent accessories
        supabase
          .from('thingproduct')
          .select('*')
          .order('id_thing', { ascending: false })
          .limit(5)
      ]);

      const allProducts = [];

      // Process books
      if (booksData.data) {
        booksData.data.forEach(book => {
          allProducts.push({
            ...book,
            product_type: 'book',
            id_product: book.id_product,
            title_product: book.title_product,
            image_url_product: book.image_url_product,
            language_product: book.language_product,
            pages_product: book.pages_product,
            is_book_product: true
          });
        });
      }

      // Process bibles
      if (biblesData.data) {
        biblesData.data.forEach(bible => {
          allProducts.push({
            ...bible,
            product_type: 'bible',
            id_product: bible.id_bible,
            title_product: bible.title_bible,
            image_url_product: bible.image_urls,
            language_product: bible.language_bible,
            format_product: bible.format_bible,
            pages_product: bible.pages_bible
          });
        });
      }

      // Process accessories
      if (thingsData.data) {
        thingsData.data.forEach(thing => {
          allProducts.push({
            ...thing,
            product_type: 'thing',
            id_product: thing.id_thing,
            title_product: thing.title_thing,
            image_url_product: thing.image_urls,
            language_product: null
          });
        });
      }

      // Sort all products by their respective IDs (most recent first)
      allProducts.sort((a, b) => {
        const aId = a.id_product || 0;
        const bId = b.id_product || 0;
        return bId - aId;
      });

      console.log('📊 DASHBOARD DEBUG - All recent products:', allProducts.length);
      console.log('📊 Product types:', {
        books: allProducts.filter(p => p.product_type === 'book').length,
        bibles: allProducts.filter(p => p.product_type === 'bible').length,
        things: allProducts.filter(p => p.product_type === 'thing').length
      });

      return allProducts.slice(0, 10); // Return top 10 most recent
    } catch (error) {
      console.error('❌ Error fetching recent products:', error);
      return [];
    }
  }
  static async getBooksByPriceRange() {
    const { data, error } = await supabase
      .from('productbook')
      .select('price_original_product');

    if (error) return { low: 0, medium: 0, high: 0 };

    let low = 0, medium = 0, high = 0;
    data?.forEach(book => {
      const price = book.price_original_product || 0;
      if (price <= 10) low++;
      else if (price <= 25) medium++;
      else high++;
    });

    return { low, medium, high };
  }

  static async getInventoryValue() {
    const { data, error } = await supabase
      .from('productbook')
      .select('price_original_product');

    if (error) return 0;

    return data?.reduce((sum, book) => {
      const price = book.price_original_product || 0;
      // Assuming stock of 1 since stock_count_product doesn't exist
      return sum + price;
    }, 0) || 0;
  }

  static async getLowStockBooks() {
    const { data, error } = await supabase
      .from('productbook')
      .select('*')
      .eq('is_book_product', true)
      .order('id_product', { ascending: true })
      .limit(5); // Show first 5 books since we can't check stock
    return error ? [] : data;
  }

  static async getDashboardStats() {
    try {
      const [
        totalBooks,
        totalAuthors,
        totalCategories,
        booksOnSale,
        recentBooks,
        lowStockBooks,
        inventoryValue,
        categoryStats,
        priceRangeStats,
        authorStats
      ] = await Promise.all([
        this.getTotalBooks(),
        this.getTotalAuthors(),
        this.getTotalCategories(),
        this.getBooksOnSale(),
        this.getRecentBooks(),
        this.getLowStockBooks(),
        this.getInventoryValue(),
        this.getBooksByCategory(),
        this.getBooksByPriceRange(),
        this.getBooksByAuthor()
      ]);

      return {
        totalBooks,
        totalAuthors,
        totalCategories,
        booksOnSale: booksOnSale.length,
        recentBooks,
        lowStockBooks,
        inventoryValue,
        categoryStats,
        priceRangeStats,
        authorStats
      };
    } catch (error) {
      console.error('Error en getDashboardStats:', error);
      return {
        totalBooks: 0,
        totalAuthors: 0,
        totalCategories: 0,
        booksOnSale: 0,
        recentBooks: [],
        lowStockBooks: [],
        inventoryValue: 0,
        categoryStats: [],
        priceRangeStats: { low: 0, medium: 0, high: 0 },
        authorStats: []
      };
    }
  }
}

export default DashboardServiceSQL;
